import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { ReactSVG } from "react-svg";
import { MdCancel } from "react-icons/md";
import {
  useCreatePopUpMutation,
  useDeletePopUpMutation,
  useGetPopUpByIdQuery,
  useUpdatePopUpMutation,
} from "../../redux/popUpApi";
import { toast } from "react-toastify";
import { showErrorToast } from "../../utils/Utils";
import { showConfirmDialog } from "../showConfirmDialog/ShowConfirmDialog";

const AddPopup = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { data, isLoading, isFetching } = useGetPopUpByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  const [createPopUp, { isLoading: createLoading }] = useCreatePopUpMutation();
  const [updatePopUp, { isLoading: updateLoading }] = useUpdatePopUpMutation();
  const [deletePopUp, { isLoading: isDeleteLoading }] =
    useDeletePopUpMutation();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
    buttonText: "",
    startDate: "",
    endDate: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode && data?.popUp) {
      setFormData({
        title: data.popUp.title || "",
        description: data.popUp.description || "",
        link: data.popUp.buttonLink || "",
        buttonText: data.popUp.buttonText || "",
        startDate: data.popUp.startDate
          ? data.popUp.startDate.split("T")[0]
          : "",
        endDate: data.popUp.endDate ? data.popUp.endDate.split("T")[0] : "",
      });

      if (data.popUp.media?.mediaUrl) {
        setFile({
          src: data.popUp.media.mediaUrl,
          file: null,
          fileName: data.popUp.media.fileName,
          fileType: data.popUp.media.fileType,
          fileSize: data.popUp.media.fileSize,
        });
      }
    }
  }, [isEditMode, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  const handleFile = (uploadedFile) => {
    if (!uploadedFile) return;

    const validTypes = ["image/", "video/"];
    const isValid = validTypes.some((type) =>
      uploadedFile.type.startsWith(type)
    );

    if (!isValid) {
      setError("Only image and video files are allowed.");
      setFile(null);
      return;
    }

    if (uploadedFile.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10MB limit.");
      setFile(null);
      return;
    }

    setError("");
    const src = URL.createObjectURL(uploadedFile);
    setFile({
      file: uploadedFile,
      src,
      fileName: uploadedFile.name,
      fileType: uploadedFile.type,
      fileSize: uploadedFile.size,
    });
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      description: "",
      link: "",
      buttonText: "",
      startDate: "",
      endDate: "",
    });
    setFile(null);
    setError("");
    setErrors({});
  };
  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDelete = () => {
    setFile(null);
    setError("");
    fileInputRef.current.value = "";
  };

  const formatFileSize = (size) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    setError("");

    if (!formData.title.trim()) {
      newErrors.title = "Title is required.";
      isValid = false;
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required.";
      isValid = false;
    }
    if (!formData.startDate.trim()) {
      newErrors.startDate = "Start date is required.";
      isValid = false;
    }
    if (!formData.endDate.trim()) {
      newErrors.endDate = "End date is required.";
      isValid = false;
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (end < start) {
        newErrors.endDate = "End date must be greater than start date.";
        isValid = false;
      }
    }

    const hasButtonLink = formData.link?.trim();
    const hasButtonText = formData.buttonText?.trim();

    if (hasButtonLink && !hasButtonText) {
      newErrors.buttonText = "Button text is required when link is provided.";
      isValid = false;
    } else if (!hasButtonLink && hasButtonText) {
      newErrors.link = "Button link is required when text is provided.";
      isValid = false;
    }

    if (!isEditMode) {
      if (!file) {
        setError("Please upload an File.");
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };
  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const popUpData = new FormData();
      popUpData.append("title", formData.title);
      popUpData.append("description", formData.description);
      popUpData.append("buttonLink", formData.link);
      popUpData.append("buttonText", formData.buttonText);
      popUpData.append("startDate", formData.startDate);
      popUpData.append("endDate", formData.endDate);
      popUpData.append("media", file?.file);
      try {
        let response = null;
        if (isEditMode) {
          response = await updatePopUp({ id, popUpData: popUpData }).unwrap();
        } else {
          response = await createPopUp(popUpData).unwrap();
        }

        if (response.success) {
          handleCancel();
          toast.success(response.message);
          navigate("/announcement-popup");
        }
      } catch (err) {
        showErrorToast(err);
      }
    }
  };

  const handleDeletePopUp = async (id) => {
    try {
      const response = await deletePopUp(id).unwrap();
      if (response.success) {
        navigate("/announcement-popup");
        toast.success(response.message);
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  const handleDeleteConfrim = (id) => {
    showConfirmDialog({
      title: "Delete Pop-Up",
      message: "Are you sure you want to delete this Pop-Up?",
      type: "danger",
      onConfirm: () => {
        handleDeletePopUp(id);
      },
    });
  };
  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        {isEditMode ? "Update" : "Add"} Announcements
      </h1>
      {isLoading || isFetching ? (
        <AnnouncementFormSkeleton />
      ) : (
        <form
          className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col md:gap-1 gap-[2px] ">
            <label className="font-semibold">Title</label>
            <input
              type="text"
              name="title"
              value={formData?.title}
              onChange={handleChange}
              placeholder="Enter announcement here"
              className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
            />
            {errors.title && (
              <p className="text-red-500 md:text-sm text-[12px]">
                {errors.title}
              </p>
            )}
          </div>
          <div className="flex flex-col md:gap-1 gap-[2px] ">
            <label className="font-semibold">Description</label>
            <textarea
              name="description"
              rows="4"
              value={formData?.description}
              onChange={handleChange}
              placeholder="Enter title here"
              className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
            ></textarea>

            {errors.description && (
              <p className="text-red-500 md:text-sm text-[12px] ">
                {errors.description}
              </p>
            )}
          </div>
          <div className=" grid md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-3 gap-2">
            <div className="flex flex-col md:gap-1 gap-[2px] ">
              <label className="font-semibold">Link URL (Optional)</label>
              <input
                type="url"
                name="link"
                value={formData?.link}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.link && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.link}
                </p>
              )}
            </div>
            <div className="flex flex-col md:gap-1 gap-[2px] ">
              <label className="font-semibold">Link Button Text</label>
              <input
                type="text"
                name="buttonText"
                value={formData?.buttonText}
                onChange={handleChange}
                placeholder="Learn More"
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.buttonText && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.buttonText}
                </p>
              )}
            </div>
          </div>
          <div className=" grid md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-3 gap-2">
            <div className="flex flex-col md:gap-1 gap-[2px] ">
              <label className="font-semibold">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                placeholder="Start Date"
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.startDate && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.startDate}
                </p>
              )}
            </div>
            <div className="flex flex-col md:gap-1 gap-[2px] ">
              <label className="font-semibold">End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                placeholder="End Date"
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.endDate && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.endDate}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col md:gap-1 gap-[2px]">
            <div className="font-semibold">Upload Image or Video</div>

            <div
              className="w-full border border-dashed border-[#00000033] md:p-4 p-3 rounded-xl"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Upload Area */}
              {!file && (
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col md:gap-2 gap-1 md:py-4 py-2 items-center w-full"
                >
                  <div className="md:p-3 p-1.5 bg-[#02643B20] rounded-full">
                    <ReactSVG
                      src="/icon/upload file.svg"
                      className="lg:size-10 md:size-8 size-6"
                    />
                  </div>

                  <p className="text-center font-semibold text-[#0B0B0B]">
                    Drop your files here or{" "}
                    <span className="text-[#00643b]">Click to upload</span>
                  </p>

                  <p className="text-[#6D6D6D] text-center md:text-sm text-[12px]">
                    JPG, PNG, GIF, MP4, MOV, AVI etc.
                  </p>
                  <p className="text-[#6D6D6D] md:text-sm text-[12px] text-center">
                    Max file size is 10MB
                  </p>
                </label>
              )}

              <input
                id="file-upload"
                type="file"
                accept="image/*,video/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />

              {/* File Preview */}
              {file && (
                <div className="flex flex-col gap-3 ">
                  <div className="flex justify-between items-center text-sm text-[#6D6D6D]">
                    <span className="truncate max-w-[70%]">
                      {file.fileName}
                    </span>
                    <span>{formatFileSize(file.fileSize)}</span>
                  </div>
                  <div className="relative">
                    {file.fileType.startsWith("image/") ? (
                      <img
                        src={file.src}
                        alt="Upload Preview"
                        className="object-cover w-full md:rounded-[1rem] rounded-lg"
                      />
                    ) : (
                      <video
                        src={file.src}
                        controls
                        className="object-cover w-full md:rounded-[1rem] rounded-lg"
                      />
                    )}

                    <button
                      onClick={handleDelete}
                      className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full shadow"
                    >
                      <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p className="text-red-500 md:text-sm text-[12px] mt-1">
                {error}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={() => handleCancel()}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>
            {isEditMode ? (
              <div
                onClick={() => handleDeleteConfrim(id)}
                className={`lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border text-center border-[#D80000] flex items-center justify-center text-white bg-[#D80000] lg:rounded-xl md:rounded-lg rounded-md font-medium`}
              >
                {isDeleteLoading ? (
                  <ButtonLoading text=" Delete..." />
                ) : (
                  "Delete"
                )}
              </div>
            ) : (
              ""
            )}
            <button
              type="submit"
              disabled={createLoading || updateLoading ? true : false}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                updateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : createLoading ? (
                <ButtonLoading text="Publish..." />
              ) : (
                "Publish"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const AnnouncementFormSkeleton = () => {
  return (
    <div className="animate-pulse flex flex-col lg:gap-4 md:gap-3 gap-2">
      {/* Title */}
      <div>
        <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
        <div className="h-10 bg-gray-200 rounded w-full" />
      </div>

      {/* Description */}
      <div>
        <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
        <div className="h-24 bg-gray-200 rounded w-full" />
      </div>

      {/* Link URL + Button Text */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
        <div>
          <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
      </div>

      {/* Start & End Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
        <div>
          <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
      </div>

      {/* Upload box */}
      <div>
        <div className="h-4 w-40 bg-gray-200 rounded mb-3" />
        <div className="h-40 bg-gray-200 rounded-lg border border-gray-300" />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <div className="h-10 w-24 bg-gray-200 rounded-lg" />
        <div className="h-10 w-24 bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
};

export default AddPopup;
